import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';

const InfoWindowCard = ({ details, onClose }) => {
  const navigation = useNavigation<any>();
  const speed = details.status?.speed || 0;
  const signal = details.status?.signal || null;
  const timestamp = details.status?.status_last_updated ? new Date(details.status.status_last_updated).toLocaleString() : null;
  const powerConnected = details.status?.charge ? 'Power Connected' : 'On Battery';
  const reservation = details.currentReservation;

  const reservationBadgeColors = (statusLabel?: string) => {
    const normalized = (statusLabel || '').toLowerCase();
    if (normalized.includes('confirm')) return { bg: '#c8f0d6', fg: '#0f7a2a' };
    if (normalized.includes('pend')) return { bg: '#ffe7c2', fg: '#b45309' };
    if (normalized.includes('cancel') || normalized.includes('fail')) return { bg: '#fbd0d0', fg: '#b91c1c' };
    return { bg: '#cdeafe', fg: '#0b8ac9' };
  };

  const stageBadgeColors = (stage?: any) => {
    switch (stage) {
      case 1:
      case '1':
        return { bg: '#262E32', fg: '#ffffff' };
      case 2:
      case '2':
        return { bg: '#4379FF', fg: '#ffffff' };
      case 3:
      case '3':
        return { bg: '#B8393C', fg: '#ffffff' };
      case 4:
      case '4':
        return { bg: '#4CBA70', fg: '#ffffff' };
      case 5:
      case '5':
        return { bg: '#666', fg: '#ffffff' };
      default:
        return null;
    }
  };

  const formatDate = (value?: any) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString();
  };

  const reservationDateRange = () => {
    if (!reservation) return null;
    const start =
      reservation.startDate ||
      reservation.start_date ||
      reservation.start ||
      reservation.from;
    const end =
      reservation.endDate ||
      reservation.end_date ||
      reservation.end ||
      reservation.to;
    const formattedStart = formatDate(start);
    const formattedEnd = formatDate(end);
    if (formattedStart && formattedEnd) return `${formattedStart} - ${formattedEnd}`;
    return reservation.dates || reservation.dateRange || null;
  };

  return (
    <View style={styles.card}>
      <TouchableOpacity onPress={onClose} style={styles.closeButton} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
        <FontAwesome5 name="times" size={16} color="#111827" />
      </TouchableOpacity>

      <View style={styles.headerContainer}>
        <View style={styles.avatar}>
          <Text style={styles.avatarText}>{(details.name || 'T').charAt(0).toUpperCase()}</Text>
        </View>
        <View style={{ flex: 1 }}>
          <Text style={styles.headerText}>{details.name}</Text>
          {!!details.id && <Text style={styles.subHeaderText}>ID: {details.id}</Text>}
        </View>
      </View>

      <View style={styles.statsRow}>
        <View style={styles.statChip}>
          <FontAwesome5 name="tachometer-alt" size={14} color="#0f172a" />
          <Text style={styles.statText}>{`${speed} km/h`}</Text>
        </View>
        {!!signal && (
          <View style={styles.statChip}>
            <FontAwesome5 name="signal" size={14} color="#0f172a" />
            <Text style={styles.statText}>{`${signal}%`}</Text>
          </View>
        )}
        <View style={styles.statChip}>
          <FontAwesome5 name="bolt" size={14} color="#0f172a" />
          <Text style={styles.statText}>{powerConnected}</Text>
        </View>
      </View>

      <View style={[styles.infoContainer, { marginTop: 10 }]}>
        {timestamp && (
          <View style={styles.infoRow}>
            <FontAwesome5 name="map-marker-alt" size={14} color="#475569" />
            <Text style={styles.infoText}>{timestamp}</Text>
          </View>
        )}
        {reservation && (
          <>
            <View style={styles.infoRow}>
              <FontAwesome5 name="calendar-alt" size={14} color="#475569" />
              <Text style={styles.infoText}>{reservationDateRange() || 'Active reservation'}</Text>
            </View>
            {!!(reservation.display_names || reservation.families) && (
              <View style={styles.infoRow}>
                <FontAwesome5 name="cube" size={14} color="#475569" />
                <Text style={styles.infoText}>{reservation.display_names || reservation.families}</Text>
              </View>
            )}
            {!!reservation.order_number && (
              <View style={styles.infoRow}>
                <FontAwesome5 name="hashtag" size={14} color="#475569" />
                <Text style={styles.infoText}>{reservation.order_number}</Text>
              </View>
            )}
            {!!reservation.status_text && (
              <View style={[styles.infoRow, styles.badgeRow]}>
                <FontAwesome5 name="tag" size={12} color="#475569" />
                <Text
                  style={[
                    styles.statusBadge,
                    (() => {
                      const stageColors = stageBadgeColors(reservation.stage);
                      const statusColors = reservationBadgeColors(reservation.status_text);
                      const palette = stageColors || statusColors;
                      return { backgroundColor: palette.bg, color: palette.fg };
                    })(),
                  ]}
                >
                  {reservation.status_text}
                </Text>
              </View>
            )}
          </>
        )}
        <View style={styles.detailButtonContainer}>
          <TouchableOpacity
            style={styles.detailButton}
            onPress={() => navigation.navigate('TrackingTags', { tag: details?.id })}
            activeOpacity={0.8}
          >
            <Text style={styles.detailButtonText}>Details</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    position: 'relative',
    backgroundColor: 'white',
    width: '100%',
    minWidth: 220,
    padding: 12,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOpacity: 0.1,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 4 },
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
    paddingRight: 40, // leave space for close button
  },
  headerText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#0f172a',
  },
  subHeaderText: {
    fontSize: 12,
    color: '#64748b',
  },
  statsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 8,
  },
  statChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: '#f3f4f6',
    borderRadius: 10,
  },
  statText: {
    fontSize: 13,
    color: '#0f172a',
    fontWeight: '600',
  },
  infoContainer: {
    justifyContent: 'space-around',
  },
  detailButtonContainer: {
    alignItems: 'flex-end',
    marginTop: 8,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  infoText: {
    marginLeft: 8,
    fontSize: 14,
    color: '#0f172a',
  },
  iconSection: {
    justifyContent: 'space-around',
  },
  iconRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  iconButton: {
    marginHorizontal: 5,
  },
  detailButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#2b8ae3',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  detailButtonText: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: '700',
  },
  closeButton: {
    position: 'absolute',
    top: 5,
    right: 5,
    padding: 6,
    borderRadius: 999,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    zIndex: 10,
    elevation: 2,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#e0f2fe',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#2563eb',
  },
  badgeRow: {
    gap: 6,
  },
  statusBadge: {
    backgroundColor: '#dbeafe',
    color: '#1d4ed8',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    fontWeight: '600',
    fontSize: 12,
  },
});

export default InfoWindowCard;